import * as THREE from 'three';
import { ATMO, COUNTERMEASURES } from '../config/tuning';
import { DECOY } from '../config/weapons';
import type { Checksum, Checksummable } from '../core/Checksum';
import { Pool } from '../core/Pool';

/**
 * Chaff and flares (SPEC §3.7), and the decoys they put in the air.
 *
 * The design rule that matters here is in SPEC §3.6.3: **flares only work if the aircraft turns
 * away after release.** A decoy that worked on release would reduce the whole IR threat to a
 * button press, so what a flare actually does is offer the seeker a *second* heat source — and a
 * seeker only takes it once the two sources have separated enough in its field of view to tell
 * them apart and choose. Flying straight keeps the flare inside your own signature; turning is
 * what opens the angle. Punch and turn.
 *
 * Chaff is the radar equivalent, and is geometric in a different way: the cloud has to be roughly
 * between the missile and the aircraft to mask the return at all.
 *
 * Units: metres, m/s, seconds.
 */

export const DecoyKind = {
  Chaff: 'chaff',
  Flare: 'flare',
} as const;
export type DecoyKindId = (typeof DecoyKind)[keyof typeof DecoyKind];

export interface Decoy {
  kind: DecoyKindId;
  readonly positionM: THREE.Vector3;
  readonly velocityMs: THREE.Vector3;
  /** Seconds since release. */
  ageS: number;
  /** Total burn or bloom time, seconds. */
  lifetimeS: number;
  active: boolean;
}

function createDecoy(): Decoy {
  return {
    kind: DecoyKind.Chaff,
    positionM: new THREE.Vector3(),
    velocityMs: new THREE.Vector3(),
    ageS: 0,
    lifetimeS: 0,
    active: false,
  };
}

function resetDecoy(decoy: Decoy): void {
  decoy.ageS = 0;
  decoy.lifetimeS = 0;
  decoy.active = false;
  decoy.positionM.set(0, 0, 0);
  decoy.velocityMs.set(0, 0, 0);
}

/**
 * Effective radius of a chaff cloud at a given age, metres.
 *
 * The bloom is not instant: for the first third of a second the cloud is still a bundle, which is
 * why a release made *as* the missile arrives is worth much less than one made two seconds out.
 */
export function chaffRadiusM(ageS: number): number {
  const bloom = ageS / DECOY.chaffBloomTimeS;
  const grown = bloom >= 1 ? 1 : bloom;
  return COUNTERMEASURES.chaffRadiusM * grown;
}

/**
 * Infrared brightness of a flare relative to the airframe, unitless.
 *
 * Burns hot immediately and fades over its life. The fade is what makes flare timing a skill
 * rather than a stock of one-shot immunities.
 */
export function flareIntensity(ageS: number): number {
  const burn = COUNTERMEASURES.flareBurnS;
  if (ageS >= burn) return 0;
  const remaining = 1 - ageS / burn;
  return COUNTERMEASURES.flareIrGain * remaining;
}

export class Countermeasures implements Checksummable {
  chaffRemaining: number = COUNTERMEASURES.chaffCount;
  flaresRemaining: number = COUNTERMEASURES.flareCount;

  /** Live decoys. Pre-sized; swap-removal keeps it allocation-free. */
  readonly active: Decoy[] = [];

  private readonly pool = new Pool<Decoy>(
    'decoy',
    createDecoy,
    resetDecoy,
    DECOY.poolCapacity,
  );

  /**
   * Release one chaff bundle. Returns false when the dispenser is empty.
   *
   * The cloud is ejected downward and behind, and then keeps most of the aircraft's velocity for
   * a moment before the air strips it — which is why chaff works best across the missile's line
   * rather than along it.
   */
  releaseChaff(aircraftPositionM: THREE.Vector3, aircraftVelocityMs: THREE.Vector3): boolean {
    if (this.chaffRemaining <= 0) return false;
    this.chaffRemaining -= 1;

    const decoy = this.pool.acquire();
    decoy.kind = DecoyKind.Chaff;
    decoy.positionM.copy(aircraftPositionM);
    decoy.velocityMs.copy(aircraftVelocityMs).multiplyScalar(0.35);
    decoy.velocityMs.y -= DECOY.chaffEjectMs;
    decoy.ageS = 0;
    decoy.lifetimeS = COUNTERMEASURES.chaffBloomS;
    decoy.active = true;
    this.active.push(decoy);
    return true;
  }

  /** Release one flare. Returns false when the dispenser is empty. */
  releaseFlare(aircraftPositionM: THREE.Vector3, aircraftVelocityMs: THREE.Vector3): boolean {
    if (this.flaresRemaining <= 0) return false;
    this.flaresRemaining -= 1;

    const decoy = this.pool.acquire();
    decoy.kind = DecoyKind.Flare;
    decoy.positionM.copy(aircraftPositionM);
    decoy.velocityMs.copy(aircraftVelocityMs);
    decoy.velocityMs.y -= DECOY.flareEjectMs;
    decoy.ageS = 0;
    decoy.lifetimeS = COUNTERMEASURES.flareBurnS;
    decoy.active = true;
    this.active.push(decoy);
    return true;
  }

  /** Advance every live decoy and retire the spent ones. */
  tick(dtS: number): void {
    for (let i = this.active.length - 1; i >= 0; i -= 1) {
      const decoy = this.active[i];
      decoy.ageS += dtS;

      if (decoy.ageS >= decoy.lifetimeS) {
        decoy.active = false;
        // Swap-remove: order does not matter and splice would allocate.
        this.active[i] = this.active[this.active.length - 1];
        this.active.pop();
        this.pool.release(decoy);
        continue;
      }

      if (decoy.kind === DecoyKind.Flare) {
        // Flares shed the aircraft's speed and fall, which is what opens the angle a turning
        // aircraft needs. Chaff is lighter and simply drifts down with the cloud.
        const drag = Math.exp(-DECOY.flareDragPerS * dtS);
        decoy.velocityMs.multiplyScalar(drag);
        decoy.velocityMs.y -= ATMO.G * dtS;
      } else {
        decoy.velocityMs.multiplyScalar(Math.exp(-2.2 * dtS));
      }

      decoy.positionM.addScaledVector(decoy.velocityMs, dtS);
    }
  }

  /** Restock and clear the air. Called on spawn and mission restart. */
  reset(): void {
    for (let i = this.active.length - 1; i >= 0; i -= 1) {
      this.active[i].active = false;
      this.pool.release(this.active[i]);
    }
    this.active.length = 0;
    this.chaffRemaining = COUNTERMEASURES.chaffCount;
    this.flaresRemaining = COUNTERMEASURES.flareCount;
  }

  checksum(into: Checksum): void {
    into.addInt(this.chaffRemaining);
    into.addInt(this.flaresRemaining);
    into.addInt(this.active.length);
    for (let i = 0; i < this.active.length; i += 1) {
      const decoy = this.active[i];
      into.addFloat(decoy.positionM.x);
      into.addFloat(decoy.positionM.y);
      into.addFloat(decoy.positionM.z);
      into.addFloat(decoy.ageS);
    }
  }
}
